// attachment-list.component.ts
import { Component, ElementRef, Inject, InjectionToken, Input, OnInit, signal, Type, ViewChild } from '@angular/core';
import { FileInputDirective } from '@ngx-dropzone/cdk';
import { MatDropzone } from '@ngx-dropzone/material';
import { GridModule } from '@progress/kendo-angular-grid';
import { DialogRef, DialogService, DialogSettings, DialogsModule } from '@progress/kendo-angular-dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BehaviorSubject, finalize, firstValueFrom, lastValueFrom, Observable, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { nameOfFactory } from '@ticomix/common';
import { AttachmentEditComponent, IAttachmentEditComponent } from '../attachment-edit/attachment-edit.component';
import { KendoAlertService } from '../../kendo-alert';

/**
 * Interface representing an attachment entity with metadata.
 */
export interface IAttachment {
    IDAttachment: string | number;
    FileName: string | null;
    FileType: string | null;
    AttachmentLocation: string | null;
    Directory: string | null;
    Description: string | null;
    AttachmentTypeCode: string | null;
    SHA256: string | null;
    BlobId: string | null;
    AddUser: string | null;
    AddDate: Date | null;
    ChangeUser: string | null;
    ChangeDate: Date | null;
}


/**
 * Interface representing a file parameter for upload operations.
 */
export interface FileParameter {
    data: any;
    fileName: string;
}

/**
 * Interface representing a file response from download operations.
 */
export interface FileResponse {
    data: Blob;
    status: number;
    fileName?: string;
    headers?: { [name: string]: any };
}

/**
 * Injection token for the base URL used for attachment operations.
 */
export const ATTACHMENT_BASE_URL = new InjectionToken<string>('ATTACHMENT_BASE_URL');
/**
 * Injection token for the attachment client service.
 */
export const ATTACHMENT_CLIENT = new InjectionToken<IAttachmentClient>('ATTACHMENT_CLIENT');

/**
 * Interface for the attachment client service that handles CRUD operations for attachments.
 */
export interface IAttachmentClient {
    /**
     * Lists all attachments for a specific record.
     * @param tableName The name of the table containing the record.
     * @param recordID The ID of the record.
     * @returns An observable that emits an array of attachments.
     */
    listAttachment(tableName: string, recordID: any): Observable<IAttachment[]>;
    
    /**
     * Retrieves a specific attachment.
     * @param tableName The name of the table containing the record.
     * @param recordID The ID of the record.
     * @param id The ID of the attachment.
     * @returns An observable that emits the requested attachment.
     */
    getAttachment(tableName: string, recordID: string | number, id: string | number): Observable<IAttachment>;
    
    /**
     * Creates or updates an attachment.
     * @param body The attachment data to create or update.
     * @returns An observable that emits the created or updated attachment.
     */
    postAttachment(body: IAttachment | undefined): Observable<IAttachment>;
    
    /**
     * Deletes a specific attachment.
     * @param tableName The name of the table containing the record.
     * @param recordID The ID of the record.
     * @param id The ID of the attachment to delete.
     * @returns An observable that completes when the deletion is successful.
     */
    deleteAttachment(tableName: string, recordID: string | number, id: string | number): Observable<void>;
    
    /**
     * Uploads a file as an attachment for a specific record.
     * @param tableName The name of the table containing the record.
     * @param recordID The ID of the record.
     * @param file The file to upload.
     * @returns An observable that emits a progress value between 0 and 100.
     */
    uploadAttachment(tableName: string, recordID: string | number, file: FileParameter | undefined): Observable<number>;
    
    /**
     * Downloads a specific attachment.
     * @param tableName The name of the table containing the record.
     * @param recordID The ID of the record.
     * @param id The ID of the attachment to download.
     * @returns An observable that emits the file response.
     */
    downloadAttachment(tableName: string, recordID: string | number, id: string | number): Observable<FileResponse>;
}

/**
 * A component that displays and manages a list of attachments for a specific record.
 * 
 * This component provides a grid view of attachments with capabilities for uploading,
 * downloading, editing, and deleting attachments. It includes a drag-and-drop zone
 * for file uploads and progress tracking for both uploads and downloads.
 * 
 * @example
 * ```html
 * <attachment-list 
 *   tableName="Customer" 
 *   [recordId]="customerId"
 *   [displayEditButton]="true"
 *   [displayDeleteButton]="false">
 * </attachment-list>
 * ```
 */
@Component({
    selector: 'attachment-list',
    templateUrl: './attachment-list.component.html',
    styleUrls: ['./attachment-list.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatInputModule,
        MatProgressBarModule,
        GridModule,
        DialogsModule,
        FileInputDirective,
        MatChipsModule,
        MatIconModule,
        MatDropzone
    ]
})
export class AttachmentListComponent implements OnInit {
    /**
     * The name of the table containing the record for which attachments are managed.
     * @default ''
     */
    @Input() tableName = '';

    /**
     * The ID of the record for which attachments are managed.
     * @default 0
     */
    @Input() recordId = 0;

    /**
     * Whether to display the file drop zone for uploading files.
     * @default true
     */
    @Input() displayDropzone = true;

    /**
     * Whether to display the edit button for each attachment.
     * @default true
     */
    @Input() displayEditButton = true;

    /**
     * Whether to display the delete button for each attachment.
     * @default true
     */
    @Input() displayDeleteButton = true;

    /**
     * Whether to display the download button for each attachment.
     * @default true
     */
    @Input() displayDownloadButton = true;

    /**
     * The component type to use for editing attachments.
     * This allows customizing the edit dialog by providing a different component.
     * @default AttachmentEditComponent
     */
    @Input()
    editComponentType: Type<IAttachmentEditComponent> = AttachmentEditComponent;

    /**
     * Signal indicating whether data is currently being loaded.
     */
    loading = signal(false);

    /**
     * Height of the attachment grid in pixels.
     */
    gridHeight = 450;

    /**
     * The array of attachment data displayed in the grid.
     */
    attachmentData: IAttachment[] = [];

    /**
     * Reference to the nameOfFactory function for type-safe property access.
     */
    nameOfFactory = nameOfFactory;

    /**
     * Reference to the file input element for file selection.
     */
    @ViewChild('filesCtrl')
    filesCtrl!: ElementRef<HTMLInputElement>;

    /**
     * BehaviorSubject tracking the progress of the current file upload.
     */
    uploadProgress$ = new BehaviorSubject<number>(0);

    /**
     * Signal indicating whether a file upload is in progress.
     */
    isUploading = signal(false);

    /**
     * Collection of download progress trackers for multiple concurrent downloads.
     * @internal
     */
    downloadsInProgress: { 
        attachment: IAttachment, 
        progress: BehaviorSubject<number>, 
        status: BehaviorSubject<string> 
    }[] = [];

    /**
     * Reference to the current edit dialog.
     * @internal
     */
    editDialogRef?: DialogRef;

    /**
     * Configuration settings for the attachment edit dialog.
     */
    editDialogSettings: DialogSettings = {
        width: 500,
        title: 'Edit Attachment',
        content: AttachmentEditComponent,
    };

    constructor(
        protected http: HttpClient,
        protected kendoAlertService: KendoAlertService,
        @Inject(ATTACHMENT_CLIENT) protected attachmentClient: IAttachmentClient,
        @Inject(ATTACHMENT_BASE_URL) protected baseUrl: string,
        protected kendoDialogService: DialogService,
    ) { }

    /**
     * Initializes the component and loads attachment data.
     */
    async ngOnInit() {
        await this.loadGridData();
    }

    /**
     * Gets the files currently selected in the file input element.
     * @returns Array of selected File objects.
     */
    get files(): File[] {
        if (!this.filesCtrl) return [];
        const _files = this.filesCtrl.nativeElement.files;

        let files: File[] = [];
        if (_files) {
            for (let i = 0; i < _files.length; i++) {
                let file = _files.item(i);
                if (file) {
                    files.push(file);
                }
            }
        }
        return files;
    }

    /**
     * Loads attachment data for the current record.
     * Sets the loading state and handles errors.
     */
    async loadGridData() {
        this.loading.set(true);
        try {
            this.attachmentData = await firstValueFrom(this.attachmentClient.listAttachment(this.tableName, this.recordId));
        } finally {
            this.loading.set(false);
        }
    }

    /**
     * Uploads a file with progress tracking.
     * @param tableName The name of the table.
     * @param recordID The ID of the record.
     * @param file The file to upload.
     * @param reportProgress Whether to report upload progress.
     * @returns An observable that emits HTTP events for tracking progress.
     */
    uploadAttachmentWithProgress(
        tableName: string,
        recordID: number,
        file: File,
        reportProgress = true
    ): Observable<HttpEvent<number>> {
        const url = `${this.baseUrl}/api/Attachment/UploadAttachment/${encodeURIComponent(tableName)}/${recordID}`;

        const formData = new FormData();
        formData.append('file', file);

        return this.http.post<number>(url, formData, {
            reportProgress,
            observe: 'events',
            responseType: 'json'
        });
    }

    /**
     * Uploads multiple files sequentially.
     * @param files Single file or array of files to upload.
     */
    async uploadFiles(files: File | File[]) {
        let copy: File[] = Array.isArray(files) ? files : [files];

        this.filesCtrl.nativeElement.value = '';
        for (const file of copy) {
            await this.uploadFile(file);
        }
    }

    /**
     * Uploads a single file with progress tracking and error handling.
     * @param file The file to upload.
     */
    async uploadFile(file: File) {
        try {
            this.isUploading.set(true);
            this.uploadProgress$.next(0);

            await lastValueFrom(this.uploadAttachmentWithProgress(
                this.tableName,
                this.recordId,
                file
            ).pipe(
                tap((event: HttpEvent<number>) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        const progress = Math.round((100 * event.loaded) / (event.total || 1));
                        this.uploadProgress$.next(progress);
                    }
                }),
                finalize(() => this.isUploading.set(false))
            ));

            // Handle successful upload
            await this.loadGridData();
        } catch (error: any) {
            this.uploadProgress$.next(0);
            this.isUploading.set(false);
            if (error instanceof HttpErrorResponse) {
                await this.kendoAlertService.alertAsync(error.error);
            }
        }
    }

    /**
     * Downloads an attachment with progress tracking.
     * @param attachment The attachment to download.
     */
    downloadAttachment(attachment: IAttachment) {
        const url = `${this.baseUrl}/api/Attachment/DownloadAttachment/${encodeURIComponent(this.tableName)}/${this.recordId}/${attachment.IDAttachment}`;

        // Track progress for this download
        let statusObj = {
            attachment: attachment,
            progress: new BehaviorSubject<number>(0),
            status: new BehaviorSubject<string>('Downloading')
        };

        this.downloadsInProgress.push(statusObj);

        this.http.get(url, {
            reportProgress: true,
            observe: 'events',
            responseType: 'blob'
        }).pipe(
            tap((event: HttpEvent<Blob>) => {
                if (event.type === HttpEventType.DownloadProgress) {
                    const progressPercent = Math.round((100 * event.loaded) / (event.total || 1));
                    statusObj.progress.next(progressPercent);
                } else if (event.type === HttpEventType.Response) {
                    // Remove from progress tracking once download is complete
                    this.downloadsInProgress.splice(this.downloadsInProgress.indexOf(statusObj), 1);

                    const blob = event.body;
                    if (blob) {
                        this.saveAs(blob, attachment.FileName || 'download');
                    }
                }
            })
        ).subscribe({
            error: (err) => {
                if (err instanceof HttpErrorResponse) {
                    statusObj.status.next('Error: ' + err.message);
                } else {
                    statusObj.status.next('Error: ' + err);
                    console.error('Download error:', err);
                }
            }
        });
    }

    /**
     * Opens a dialog for editing an attachment.
     * @param attachment The attachment to edit.
     * @returns The instance of the edit component.
     * @internal
     */
    protected editAttachment(attachment: IAttachment) {
        this.editDialogRef = this.kendoDialogService.open(this.editDialogSettings);

        let instance = this.editDialogRef.content.instance as IAttachmentEditComponent;

        instance.attachment = attachment;
        instance.canDeleteAttachment = this.canDeleteAttachment(attachment);

        // Subscribe to output events
        instance.saveAttachmentEvent.subscribe((event: IAttachment) => this.saveAttachment(event));
        instance.attachmentRemove.subscribe((event: IAttachment) => this.deleteAttachment(event));
        instance.cancelEdit.subscribe(() => this.closeEdit());

        return instance;
    }

    /**
     * Saves changes to an attachment.
     * @param attachment The attachment with updated data.
     */
    async saveAttachment(attachment: IAttachment) {
        try {
            await firstValueFrom(this.attachmentClient.postAttachment(attachment));

            await this.loadGridData();
            this.editDialogRef?.close();
        } catch (error) {
            console.error('Error updating attachment:', error);
        }
    }

    /**
     * Determines if an attachment can be deleted.
     * @param attachment The attachment to check.
     * @returns True if the attachment can be deleted, false otherwise.
     */
    canDeleteAttachment(attachment: IAttachment): boolean {
        return attachment.IDAttachment !== undefined && attachment.IDAttachment !== null && attachment.IDAttachment !== 0 && attachment.IDAttachment !== '';
    }

    /**
     * Deletes an attachment.
     * @param attachment The attachment to delete.
     */
    async deleteAttachment(attachment: IAttachment) {
        this.loading.set(true);
        try {
            await firstValueFrom(this.attachmentClient.deleteAttachment(this.tableName, this.recordId, attachment.IDAttachment));
            this.attachmentData = this.attachmentData.filter(a => a !== attachment);
            this.closeEdit();
        } catch (error) {
            await this.kendoAlertService.alertAsync('Error deleting attachment: ' + error);
        } finally {
            this.loading.set(false);
        }
    }

    /**
     * Closes the edit dialog.
     */
    closeEdit() {
        this.editDialogRef?.close();
    }

    /**
     * Triggers a browser download for a blob with the specified filename.
     * @param blob The blob to download.
     * @param filename The name that will be used for the downloaded file.
     * @internal
     */
    private saveAs(blob: Blob, filename: string): void {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create an anchor element
        const a = document.createElement('a');
        
        // Set download attributes
        a.href = url;
        a.download = filename;
        
        // Set additional attributes for compatibility
        a.style.display = 'none';
        document.body.appendChild(a);
        
        // Trigger the download
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }
}